/**
 * Frontend Permission Helper Utility
 * Mirrors the backend permission checking logic for consistent UI behavior
 */

/**
 * Check if user has access to a specific card/module
 * @param {Object} permissions - User permissions object
 * @param {string} cardId - Card/module identifier
 * @returns {boolean}
 */
export const hasCardAccess = (permissions, cardId) => {
    if (!permissions || !permissions.cards) return false;
    return permissions.cards.includes(cardId);
};

/**
 * Check if user has a specific action permission for a card
 * @param {Object} permissions - User permissions object
 * @param {string} cardId - Card/module identifier
 * @param {string} action - Action to check (view, create, edit, delete, approve)
 * @returns {boolean}
 */
export const hasCardAction = (permissions, cardId, action) => {
    if (!permissions) return false;
    
    // First check if user has access to the card
    if (!hasCardAccess(permissions, cardId)) return false;
    
    // Check card-specific actions first (if defined)
    if (permissions.cardActions && permissions.cardActions[cardId]) {
        const cardActions = permissions.cardActions[cardId];
        if (cardActions && Array.isArray(cardActions)) {
            return cardActions.includes(action);
        }
    }
    
    // Fallback to global actions (backward compatible)
    if (permissions.actions && Array.isArray(permissions.actions)) {
        return permissions.actions.includes(action);
    }
    
    return false;
};

/**
 * Get all actions available for a specific card
 * @param {Object} permissions - User permissions object
 * @param {string} cardId - Card/module identifier
 * @returns {Array<string>}
 */
export const getCardActions = (permissions, cardId) => {
    if (!permissions || !hasCardAccess(permissions, cardId)) {
        return [];
    }
    
    // Check card-specific actions first
    if (permissions.cardActions && permissions.cardActions[cardId]) {
        const cardActions = permissions.cardActions[cardId];
        if (cardActions && Array.isArray(cardActions)) {
            return cardActions;
        }
    }
    
    // Fallback to global actions
    return permissions.actions || [];
};

/**
 * Check multiple actions at once
 * @param {Object} permissions - User permissions object
 * @param {string} cardId - Card/module identifier
 * @param {Array<string>} actions - Actions to check
 * @returns {boolean} - True if user has ALL specified actions
 */
export const hasAllCardActions = (permissions, cardId, actions) => {
    return actions.every(action => hasCardAction(permissions, cardId, action));
};

/**
 * Check if user has any of the specified actions
 * @param {Object} permissions - User permissions object
 * @param {string} cardId - Card/module identifier
 * @param {Array<string>} actions - Actions to check
 * @returns {boolean} - True if user has ANY of the specified actions
 */
export const hasAnyCardAction = (permissions, cardId, actions) => {
    return actions.some(action => hasCardAction(permissions, cardId, action));
};

/**
 * Check whether a home-page division should be rendered for this user.
 *
 * Reads the HIDDEN list, so a user with nothing configured (and every user who
 * predates the home page) sees every division. This is presentation only — the
 * module behind a division is still gated by its card permission.
 *
 * @param {Object} permissions - User permissions object
 * @param {string} sectionId - Home section identifier
 * @returns {boolean}
 */
export const isHomeSectionVisible = (permissions, sectionId) => {
    const hidden = permissions?.hiddenHomeSections;
    if (!Array.isArray(hidden)) return true;
    return !hidden.includes(sectionId);
};
